import { Button, Col, Row, Spin, Typography, Tooltip, Form } from "antd";
import _ from "lodash";
import { InfoIcon } from "components/Icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import AppLoader from "components/AppLoader";
import { FieldTypes, generateFormFields } from "utils/formUtils";

const BitBucketCreateForm = ({ setAppCreated, setFormData, formData }) => {
  const [intervalId, setIntervalId] = useState();
  const [installationId, setInstallationId] = useState();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const scopes = ["REPO_READ", "REPO_WRITE", "REPO_ADMIN", "PROJECT_ADMIN", "ADMIN_WRITE", "SYSTEM_ADMIN"];

  useEffect(() => () => clearInterval(intervalId), [intervalId]);
  useEffect(() => {
    setLoading(true);
    const { promise, cancel } = ToolsIntegrationsAPI.postBitBucketDetails({
      repoType: "BITBUCKET",
      hostUrl: formData?.hostUrl,
    });
    promise
      .then((res) => {
        if (!_.isEmpty(res)) {
          setInstallationId(res.id);
        }
      })
      .finally(() => setLoading(false));
    return cancel;
  }, [setInstallationId, formData?.hostUrl]);

  const webHookURL = useMemo(
    () =>
      `${window.location.origin}/public/webhook/bitbucket/${installationId}`,
    [installationId]
  );

  const onSave = useCallback(
    (values) => {
      const formValues = form.getFieldsValue();
      const dataToSend = {
        id: installationId,
        repoType: "BITBUCKET",
        hostUrl: formData?.hostUrl,
        secretId: formValues?.clientId,
        secretKey: formValues?.clientSecret,
        redirectUrl: webHookURL,
      };
      const { promise } = ToolsIntegrationsAPI.updateBitBucketDetails(
        installationId,
        dataToSend
      );
      promise
        .then(async (res) => {
          if (!_.isEmpty(res)) {
            setInstallationId(res.id);
            const url = formData.hostUrl
              ? `${
                  formData.hostUrl
                }/rest/oauth2/latest/authorize?client_id=${form.getFieldValue(
                  "clientId"
                )}&redirect_uri=${webHookURL}&response_type=code&scope=${form.getFieldValue("scope") ? form.getFieldValue("scope") : "REPO_READ"}`
              : "https://bitbucket.com/";

            const _window = window.open(
              url,
              "_blank",
              "width=1000,height=800,rel=noopener"
            );
            const timer = setInterval(() => {
              if (_window?.closed) {
                setAppCreated(true);
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  ...values,
                  installationId: res.id,
                  redirectUrl: webHookURL,
                }));
                clearInterval(timer);
              }
            }, 1000);
            setIntervalId(timer);
          }
        })
        .finally(() => setLoading(false));
    },
    [form, installationId, formData.hostUrl, webHookURL, setAppCreated, setFormData]
  );

  const fields = [
    {
      label: "Client ID",
      name: "clientId",
      rules: [{
         required: true, message: "Please enter your Client ID" ,
      }]
    },
    {
      label: "Client Secret",
      name: "clientSecret", 
      type: FieldTypes.PASSWORD,
      rules: [
        { required: true, message: "Please enter your Client Secret" },
      ]
    },
    {
      label: "Scope",
      name: "scope", 
      type: FieldTypes.SELECT,
      options : scopes,
      help: "Please select PROJECT_ADMIN / SYSTEM_ADMIN scope permission to fetch Bitbucket team details"
    }
  ]

  return (
    <Row>
      <Spin spinning={loading} indicator={<AppLoader />}>
        <Form form={form} onFinish={onSave}>
          <Row className="m-b-sm">
            <Col span={24} className="m-b-m">
              <Typography.Text>
                Please create an{" "}
                <Typography.Link
                  href={`${formData?.hostUrl}/plugins/servlet/applinks/listApplicationLinks`}
                  target="_blank"
                >
                  Application Link
                </Typography.Link>{" "}
                in your BitBucket Instance using this Redirect URL and add the
                respective Client ID and Client Secret
              </Typography.Text>
            </Col>
            <Col span={24}>
              <>
                Redirect URL
                <Tooltip title="Format: {Domain}/public/webhook/bitbucket/{installationId}">
                  <InfoIcon className="m-l-xs" />
                </Tooltip>
              </>
            </Col>
            <Col span={24}>
              <Typography.Text copyable={{ text: webHookURL }}>
                {webHookURL}
              </Typography.Text>
            </Col>
          </Row>
          <Col span={24} className="m-b-m">
           { generateFormFields(fields)}
          </Col>
          <Col span={24}>
            <Button htmlType="submit" type="primary">
              Authenticate On BitBucket
            </Button>
          </Col>
        </Form>
      </Spin>
    </Row>
  );
};

export { BitBucketCreateForm };
